import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  Chip,
  IconButton,
  Skeleton,
  Alert,
  useTheme,
  useMediaQuery,
  styled,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
  ArrowBackIos as ArrowBackIosIcon, 
  ArrowForwardIos as ArrowForwardIosIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { format, parseISO, addMonths, startOfMonth, getDate, getHours, isSameDay } from 'date-fns';

// Simple calendar day component with indicator
const CalendarDay = styled('div')(({ theme }) => ({
  width: '36px',
  height: '36px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  borderRadius: '50%',
  margin: '2px',
  fontSize: '0.875rem',
  cursor: 'pointer',
  '&.outside-month': {
    color: theme.palette.text.disabled,
  },
  '&.selected': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&.today': {
    fontWeight: 'bold',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  '&.has-times': {
    fontWeight: 'bold',
  },
  '&:hover:not(.disabled)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.disabled': {
    color: theme.palette.text.disabled,
    cursor: 'default',
  }
}));

// Indicator dots for morning/afternoon availability
const AvailabilityIndicator = styled('div')(({ theme, period }) => ({
  position: 'absolute',
  width: '6px',
  height: '6px',
  borderRadius: '50%',
  backgroundColor: period === 'morning' ? theme.palette.primary.main : theme.palette.secondary.main,
  bottom: '2px',
  left: period === 'morning' ? 'calc(50% - 8px)' : 'calc(50% + 2px)',
}));

const TimeSelection = ({ 
  availableTimes, 
  selectedTime, 
  onTimeSelect,
  onBack,
  loading = false 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Group times by date
  const [timesByDate, setTimesByDate] = useState({});
  const [availableDateStrings, setAvailableDateStrings] = useState([]);
  
  // Current month view (default to current month)
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  
  // For the selected date's available times
  const [selectedDate, setSelectedDate] = useState(null);

  // Process available times when they change
  useEffect(() => {
    console.log('TimeSelection: Processing available times:', availableTimes);
    
    // Always reset state when availableTimes changes, even if empty
    const grouped = {};
    const dates = [];
    
    // If we have times, process them
    if (availableTimes && availableTimes.length > 0) {
      try {
        // First deduplicate the times array based on ISO time
        console.log(`Received ${availableTimes.length} available times from API`);
        const uniqueTimes = new Map();
        
        // First pass to deduplicate by exact time (hour and minute)
        availableTimes.forEach(time => {
          if (!time || !time.date || !time.iso) {
            console.warn('Invalid time entry:', time);
            return; // Skip invalid entries
          }
          
          // Create a composite key with date and time for deduplication
          // Parse the ISO time and extract hours and minutes
          const timeDate = new Date(time.iso);
          const timeKey = `${time.date}-${timeDate.getHours()}-${timeDate.getMinutes()}`;
          
          // Use this composite key for deduplication
          if (!uniqueTimes.has(timeKey)) {
            console.log(`Adding unique time: ${timeKey}`);
            uniqueTimes.set(timeKey, time);
          } else {
            console.log(`Skipping duplicate time: ${timeKey}`);
          }
        });
        
        console.log(`After frontend deduplication: ${uniqueTimes.size} unique times`);
        
        // Now process the deduplicated times
        Array.from(uniqueTimes.values()).forEach(time => {
          const timeDate = time.date;
          if (!grouped[timeDate]) {
            grouped[timeDate] = [];
            dates.push(timeDate);
          }
          grouped[timeDate].push(time);
        });
        
        // Sort times for each date
        Object.keys(grouped).forEach(date => {
          grouped[date].sort((a, b) => {
            return parseISO(a.iso).getTime() - parseISO(b.iso).getTime();
          });
        });
        
        // Set initial month based on first available date
        if (dates.length > 0) {
          setCurrentMonth(startOfMonth(parseISO(dates[0])));
          setSelectedDate(dates[0]);
        }
      } catch (err) {
        console.error('Error processing available times:', err);
      }
    } else {
      // Reset selected date if we have no times
      setSelectedDate(null);
      
      // Set month to current month if we have no dates
      setCurrentMonth(startOfMonth(new Date()));
    }
    
    setTimesByDate(grouped);
    setAvailableDateStrings(dates.sort());
    
    console.log('TimeSelection: Processed dates:', dates.length, 'with times:', Object.keys(grouped).length);
  }, [availableTimes]);

  // Check if a date has morning times (before noon)
  const hasMorningTimes = (dateStr) => {
    if (!timesByDate[dateStr]) return false;
    return timesByDate[dateStr].some(time => {
      const hour = getHours(new Date(time.iso));
      return hour < 12;
    });
  };

  // Check if a date has afternoon times (noon or after)
  const hasAfternoonTimes = (dateStr) => {
    if (!timesByDate[dateStr]) return false;
    return timesByDate[dateStr].some(time => {
      const hour = getHours(new Date(time.iso));
      return hour >= 12;
    });
  };

  // Generate calendar days for the current month
  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    // Get days in the current month
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Get the first day of the month (0-6, Sunday-Saturday)
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    // Calculate days from previous month to display
    const daysFromPrevMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Days array to hold all calendar days
    const days = [];
    
    // Add days from previous month
    const prevMonth = new Date(year, month, 0);
    const prevMonthDays = prevMonth.getDate();
    
    for (let i = prevMonthDays - daysFromPrevMonth + 1; i <= prevMonthDays; i++) {
      days.push({
        date: new Date(prevMonth.getFullYear(), prevMonth.getMonth(), i),
        outsideMonth: true
      });
    }
    
    // Add days from current month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        outsideMonth: false
      });
    }
    
    // Calculate remaining days to add from next month (to complete the grid)
    const totalDays = days.length;
    const remainingDays = 42 - totalDays; // 6 rows of 7 days
    
    // Add days from next month
    const nextMonth = new Date(year, month + 1, 1);
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(nextMonth.getFullYear(), nextMonth.getMonth(), i),
        outsideMonth: true
      });
    }
    
    return days;
  };

  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentMonth(prev => {
      const prevMonth = new Date(prev);
      prevMonth.setMonth(prevMonth.getMonth() - 1);
      return prevMonth;
    });
  };

  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentMonth(prev => {
      const nextMonth = new Date(prev);
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      return nextMonth;
    });
  };

  // Handle date selection
  const handleDateSelect = (day) => {
    const formattedDate = format(day, 'yyyy-MM-dd');
    if (timesByDate[formattedDate]) {
      setSelectedDate(formattedDate);
    }
  };

  // Check if a date is today
  const isToday = (date) => {
    const today = new Date();
    return isSameDay(date, today);
  };

  // Render time slots for selected date
  const renderTimeSlots = () => {
    if (!selectedDate || !timesByDate[selectedDate]) return null;
    
    const times = timesByDate[selectedDate];
    
    // Group times by morning and afternoon
    const morningTimes = [];
    const afternoonTimes = [];
    
    times.forEach(time => {
      const hour = getHours(new Date(time.iso));
      if (hour < 12) {
        morningTimes.push(time);
      } else {
        afternoonTimes.push(time);
      }
    });
    
    return (
      <Box>
        {/* Morning slots */}
        {morningTimes.length > 0 && (
          <Box mb={3}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1.5 
            }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                bgcolor: theme.palette.primary.main, 
                mr: 1 
              }} />
              <Typography variant="subtitle2" fontWeight="medium" color="primary.main">
                Morning
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {morningTimes.map((time) => {
                const isSelected = selectedTime && selectedTime.iso === time.iso;
                // TIMEZONE FIX: Always display times in Melbourne timezone
                // This ensures all users see the same time regardless of their location
                const timeObj = new Date(time.iso);
                const timeString = new Intl.DateTimeFormat('en-AU', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'Australia/Melbourne'
                }).format(timeObj);
                
                // Create an enhanced time object with the displayed time string
                const enhancedTime = {
                  ...time,
                  timeString: timeString, // Add the formatted time string
                  time_display: `${format(parseISO(time.date), 'EEEE, MMMM d, yyyy')} at ${timeString}`
                };
                
                return (
                  <Grid item xs={6} sm={4} lg={3} key={time.iso}>
                    <Button
                      variant={isSelected ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => onTimeSelect(enhancedTime)}
                      fullWidth
                      sx={{ 
                        minHeight: { xs: 90, sm: 85 },
                        height: 'auto',
                        py: 1.5,
                        px: 0.5,
                        textTransform: 'none',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        },
                        ...(isSelected ? {
                          fontWeight: 'bold'
                        } : {})
                      }}
                    >
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography 
                          variant="subtitle2" 
                          component="div"
                          sx={{ 
                            fontWeight: isSelected ? 'bold' : 'medium',
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                          }}
                        >
                          {timeString}
                        </Typography>
                        {time.original_practitioner_id && time.practitioner_name && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            component="div" 
                            sx={{ 
                              mt: 0.5,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              lineHeight: 1.3,
                              wordBreak: 'break-word',
                              px: 0.5
                            }}
                          >
                            {time.practitioner_name}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
        
        {/* Afternoon slots */}
        {afternoonTimes.length > 0 && (
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 1.5 
            }}>
              <Box sx={{ 
                width: 10, 
                height: 10, 
                borderRadius: '50%', 
                bgcolor: theme.palette.secondary.main, 
                mr: 1 
              }} />
              <Typography variant="subtitle2" fontWeight="medium" color="secondary.main">
                Afternoon
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              {afternoonTimes.map((time) => {
                const isSelected = selectedTime && selectedTime.iso === time.iso;
                // TIMEZONE FIX: Always display times in Melbourne timezone
                // This ensures all users see the same time regardless of their location
                const timeObj = new Date(time.iso);
                const timeString = new Intl.DateTimeFormat('en-AU', {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                  timeZone: 'Australia/Melbourne'
                }).format(timeObj);
                
                // Create an enhanced time object with the displayed time string
                const enhancedTime = {
                  ...time,
                  timeString: timeString, // Add the formatted time string
                  time_display: `${format(parseISO(time.date), 'EEEE, MMMM d, yyyy')} at ${timeString}`
                };
                
                return (
                  <Grid item xs={6} sm={4} lg={3} key={time.iso}>
                    <Button
                      variant={isSelected ? "contained" : "outlined"}
                      color="secondary"
                      onClick={() => onTimeSelect(enhancedTime)}
                      fullWidth
                      sx={{ 
                        minHeight: { xs: 90, sm: 85 },
                        height: 'auto',
                        py: 1.5,
                        px: 0.5,
                        textTransform: 'none',
                        borderRadius: 2,
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        },
                        ...(isSelected ? {
                          fontWeight: 'bold'
                        } : {})
                      }}
                    >
                      <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography 
                          variant="subtitle2" 
                          component="div"
                          sx={{ 
                            fontWeight: isSelected ? 'bold' : 'medium',
                            fontSize: { xs: '0.875rem', sm: '0.9375rem' }
                          }}
                        >
                          {timeString}
                        </Typography>
                        {time.original_practitioner_id && time.practitioner_name && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            component="div" 
                            sx={{ 
                              mt: 0.5,
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              lineHeight: 1.3,
                              wordBreak: 'break-word',
                              px: 0.5
                            }}
                          >
                            {time.practitioner_name}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
        
        {/* If no morning or afternoon times */}
        {morningTimes.length === 0 && afternoonTimes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No available times for this date.
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Time
        </Typography>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 3 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={6} sm={4} md={3} key={item}>
              <Skeleton variant="rectangular" height={60} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }
  
  if (!availableTimes || availableTimes.length === 0) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ mr: 2 }}
            color="primary"
            variant="outlined"
          >
            Back
          </Button>
          <Typography variant="h5" fontWeight="medium">
            Appointment Availability
          </Typography>
        </Box>
        
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mb: 4 }}>
          <Alert 
            severity="info" 
            variant="filled" 
            sx={{ 
              mb: 3, 
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '2rem'
              }
            }}
          >
            <Typography variant="h6">
              No Available Appointment Times
            </Typography>
          </Alert>
          
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom color="primary.main">
            This could be due to one of the following reasons:
          </Typography>
          
          <Box sx={{ mt: 2, pl: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: theme.palette.primary.main, 
                mt: 1,
                mr: 1 
              }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Cliniko Configuration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This appointment type may not be properly configured for online booking in Cliniko
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: theme.palette.primary.main, 
                mt: 1,
                mr: 1 
              }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  Full Schedule
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  The practitioner might be fully booked for the coming days
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: theme.palette.primary.main, 
                mt: 1,
                mr: 1 
              }} />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  System Integration
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There might be an issue with the connection to the Cliniko appointment system
                </Typography>
              </Box>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button 
              variant="contained" 
              color="primary" 
              size="large"
              onClick={onBack}
            >
              Try Another Practitioner
            </Button>
          </Box>
        </Paper>
        
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
          <Typography variant="subtitle1" fontWeight="bold" color="warning.dark" gutterBottom>
            For Clinic Administrators:
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please check your Cliniko settings to ensure:
          </Typography>
          <Box sx={{ pl: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: theme.palette.warning.main, 
                mr: 1 
              }} />
              <Typography variant="body2">
                Online booking is enabled for your business
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: theme.palette.warning.main, 
                mr: 1 
              }} />
              <Typography variant="body2">
                Online booking is enabled for this appointment type
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: theme.palette.warning.main, 
                mr: 1 
              }} />
              <Typography variant="body2">
                Online booking is enabled for this practitioner
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: theme.palette.warning.main, 
                mr: 1 
              }} />
              <Typography variant="body2">
                The appointment type is assigned to the practitioner
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mr: 2 }}
          color="primary"
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h5" fontWeight="medium">
          Choose Your Appointment Time
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Calendar section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Button 
                onClick={handlePrevMonth} 
                startIcon={<ArrowBackIosIcon fontSize="small" />}
                size="small"
                variant="outlined"
                color="primary"
              >
                Prev
              </Button>
              <Typography variant="h6" fontWeight="bold">
                {format(currentMonth, 'MMMM yyyy')}
              </Typography>
              <Button 
                onClick={handleNextMonth} 
                endIcon={<ArrowForwardIosIcon fontSize="small" />}
                size="small"
                variant="outlined"
                color="primary"
              >
                Next
              </Button>
            </Box>

            {/* Weekday headers */}
            <Grid container justifyContent="center">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                <Grid item key={day} xs={1.7}>
                  <Typography 
                    variant="subtitle2" 
                    align="center" 
                    sx={{ fontWeight: 'bold', mb: 1 }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar grid */}
            <Grid container justifyContent="center">
              {getDaysInMonth().map((dayObj, index) => {
                const day = dayObj.date;
                const dateStr = format(day, 'yyyy-MM-dd');
                const hasTimes = !!timesByDate[dateStr];
                const hasMorning = hasMorningTimes(dateStr);
                const hasAfternoon = hasAfternoonTimes(dateStr);
                const isSelected = selectedDate === dateStr;
                
                return (
                  <Grid item key={index} xs={1.7}>
                    <Box display="flex" justifyContent="center">
                      <CalendarDay
                        className={`
                          ${dayObj.outsideMonth ? 'outside-month' : ''}
                          ${isSelected ? 'selected' : ''}
                          ${isToday(day) ? 'today' : ''}
                          ${hasTimes ? 'has-times' : ''}
                          ${!hasTimes ? 'disabled' : ''}
                        `}
                        onClick={() => hasTimes && handleDateSelect(day)}
                        sx={{
                          transition: 'all 0.2s ease',
                          '&.has-times': {
                            '&:hover': {
                              transform: 'scale(1.15)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }
                          }
                        }}
                      >
                        {getDate(day)}
                        {hasMorning && <AvailabilityIndicator period="morning" />}
                        {hasAfternoon && <AvailabilityIndicator period="afternoon" />}
                      </CalendarDay>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  width: 10, 
                  height: 10, 
                  bgcolor: theme.palette.primary.main, 
                  borderRadius: '50%', 
                  mr: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }} />
                <Typography variant="caption" fontWeight="medium">Morning</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ 
                  width: 10, 
                  height: 10, 
                  bgcolor: theme.palette.secondary.main, 
                  borderRadius: '50%', 
                  mr: 1,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                }} />
                <Typography variant="caption" fontWeight="medium">Afternoon</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Time slots section */}
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: '100%', bgcolor: alpha(theme.palette.background.paper, 0.7) }}>
            {selectedDate ? (
              <>
                <Typography variant="h6" fontWeight="bold" gutterBottom color="primary.main" sx={{ mb: 2 }}>
                  {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
                </Typography>
                
                {timesByDate[selectedDate] ? (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Select a time slot below:
                    </Typography>
                    {renderTimeSlots()}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
                    <Typography color="text.secondary" variant="subtitle1">
                      No available times for the selected date.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Please choose another date from the calendar.
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
                <Typography variant="h6" color="text.secondary" align="center">
                  Select a date to view available times
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} align="center">
                  Dates with colored dots have available appointment times
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {selectedTime && (
        <Paper elevation={3} sx={{ mt: 4, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.05), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Selected Appointment:
            </Typography>
            <Typography variant="body1" color="primary.main" fontWeight="medium">
              {selectedTime.formatted}
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            color="primary"
            onClick={() => onTimeSelect(selectedTime)}
            size="large"
          >
            Confirm Time
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default TimeSelection;